import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { SocialPostFormData } from '../types';
import {
  SOCIAL_PLATFORMS,
  POST_TYPES,
  CAMPAIGN_GOAL_OPTIONS,
  CAPTION_LENGTHS,
  BRAND_VOICES,
  TARGET_AUDIENCE_PRESETS,
  SOCIAL_POST_THEME_OPTIONS,
  REFERENCE_STYLE_OPTIONS,
  IDENTITY_TYPE_OPTIONS,
  CREATOR_PERSONALITY_OPTIONS,
  CREATOR_TONE_OPTIONS,
  CONTENT_STYLE_OPTIONS,
  SOCIAL_POST_COST,
  SOCIAL_IMAGE_COST,
} from '../types';
import type { BusinessProfile } from '@/hooks/useBusinessProfiles';
import { IMAGE_STYLES, IMAGE_STYLE_CATEGORIES, getImageStyleConfig } from '@/config/imageStyles';
import type { ImageStyleCategory } from '@/config/imageStyles';

interface SocialPostFormProps {
  profiles: BusinessProfile[];
  profilesLoading: boolean;
  formData: SocialPostFormData;
  onChange: (data: SocialPostFormData) => void;
  onGenerate: () => void;
  onStopGeneration: () => void;
  isGenerating: boolean;
  creditsRemaining: number;
  hasEnoughCredits: (cost: number) => boolean;
  selectedProfileId?: string | null;
  onProfileSelect?: (profileId: string | null) => void;
}

const inputClass =
  'w-full text-sm px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200';

function CollapsibleSection({
  icon,
  title,
  subtitle,
  badge,
  defaultOpen = false,
  children,
}: {
  icon: string;
  title: string;
  subtitle: string;
  badge?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-stone-200 overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3.5 bg-stone-50 hover:bg-stone-100 transition-colors cursor-pointer text-left"
      >
        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-stone-200 shrink-0">
          <i className={`${icon} text-stone-500 text-sm`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-stone-800">{title}</span>
            {badge !== undefined && badge > 0 && (
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-orange-500 text-white text-xs font-bold leading-none">
                {badge}
              </span>
            )}
          </div>
          <span className="text-xs text-stone-400">{subtitle}</span>
        </div>
        <i
          className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-stone-400 text-lg shrink-0 transition-transform duration-200`}
        />
      </button>
      {open && (
        <div className="px-4 py-4 bg-white border-t border-stone-100 space-y-4">{children}</div>
      )}
    </div>
  );
}

// ── Style Picker Modal ──────────────────────────────────────────────────────
function StylePickerModal({
  selected,
  onSelect,
  onClose,
}: {
  selected: string;
  onSelect: (id: string) => void;
  onClose: () => void;
}) {
  const [activeCategory, setActiveCategory] = useState<ImageStyleCategory>('all');

  const filtered =
    activeCategory === 'all'
      ? IMAGE_STYLES
      : IMAGE_STYLES.filter((s) => s.category === activeCategory);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100 shrink-0">
          <div>
            <h3 className="text-sm font-bold text-stone-900">Choose Image Style</h3>
            <p className="text-xs text-stone-400 mt-0.5">Select the visual style for your post image</p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        {/* Category tabs */}
        <div className="flex gap-1.5 px-5 py-3 border-b border-stone-100 shrink-0 overflow-x-auto">
          {IMAGE_STYLE_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all duration-200 cursor-pointer ${
                activeCategory === cat.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="overflow-y-auto flex-1 p-4">
          <div className="grid grid-cols-3 gap-3">
            {filtered.map((style) => {
              const isSelected = selected === style.id;
              return (
                <button
                  key={style.id}
                  onClick={() => { onSelect(style.id); onClose(); }}
                  className={`group relative rounded-xl overflow-hidden border-2 transition-all duration-200 cursor-pointer text-left ${
                    isSelected
                      ? 'border-orange-500 ring-2 ring-orange-200'
                      : 'border-stone-200 hover:border-orange-300'
                  }`}
                >
                  <div className="w-full aspect-square bg-stone-100">
                    <img
                      src={style.previewImage}
                      alt={style.label}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="px-2.5 py-2 bg-white">
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-xs font-bold text-stone-800 truncate">{style.label}</span>
                      {isSelected && (
                        <div className="w-4 h-4 flex items-center justify-center rounded-full bg-orange-500 shrink-0">
                          <i className="ri-check-line text-white text-xs" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-stone-400 mt-0.5 leading-tight line-clamp-2">{style.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        {selected && (
          <div className="px-5 py-3 border-t border-stone-100 shrink-0 flex items-center justify-between bg-stone-50">
            <div className="flex items-center gap-2">
              <i className="ri-check-double-line text-orange-500 text-sm" />
              <span className="text-xs font-semibold text-stone-700">
                {getImageStyleConfig(selected)?.label ?? 'Style selected'}
              </span>
            </div>
            <button
              onClick={() => { onSelect(''); onClose(); }}
              className="text-xs text-stone-400 hover:text-stone-600 cursor-pointer"
            >
              Clear selection
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Form ───────────────────────────────────────────────────────────────
export default function SocialPostForm({
  profiles,
  profilesLoading,
  formData,
  onChange,
  onGenerate,
  onStopGeneration,
  isGenerating,
  creditsRemaining,
  hasEnoughCredits,
  selectedProfileId,
  onProfileSelect,
}: SocialPostFormProps) {
  const [showStylePicker, setShowStylePicker] = useState(false);
  const referenceImageInputRef = useRef<HTMLInputElement>(null);

  const set = (field: keyof SocialPostFormData, value: string | boolean) => {
    if (field === 'businessName') onProfileSelect?.(null);
    onChange({ ...formData, [field]: value });
  };

  const toggleAutoMode = () => {
    onChange({ ...formData, autoMode: !formData.autoMode });
  };

  const totalCost = SOCIAL_POST_COST + (formData.includeImage ? SOCIAL_IMAGE_COST : 0);
  const canGenerate = formData.autoMode
    ? hasEnoughCredits(totalCost)
    : formData.businessName.trim() &&
      formData.platform &&
      formData.postType &&
      hasEnoughCredits(totalCost);

  const handleReferenceImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target?.result as string;
      // Strip the data URL prefix to get pure base64
      const pureBase64 = base64.split(',')[1] ?? base64;
      onChange({ ...formData, referenceImageBase64: pureBase64 });
    };
    reader.readAsDataURL(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const clearReferenceImage = () => {
    onChange({ ...formData, referenceImageBase64: null, referenceImagePrompt: '' });
  };

  const advancedBadge = [
    formData.targetAudience || formData.targetAudienceType,
    formData.brandVoice,
    formData.creativeDirection,
    formData.referenceImageBase64,
  ].filter(Boolean).length;

  const extrasBadge = [formData.cta, formData.hashtags, formData.specialInstructions].filter(
    Boolean
  ).length;

  const selectedStyleConfig = formData.imageStyle ? getImageStyleConfig(formData.imageStyle) : null;

  // Handle audience preset selection
  const handleAudiencePreset = (presetId: string) => {
    if (presetId === 'niche') {
      // Toggle niche — just set the type, let user type in the text field
      const newType = formData.targetAudienceType === 'niche' ? '' : 'niche';
      onChange({ ...formData, targetAudienceType: newType });
    } else {
      const preset = TARGET_AUDIENCE_PRESETS.find((p) => p.id === presetId);
      if (!preset) return;
      const isActive = formData.targetAudienceType === presetId;
      onChange({
        ...formData,
        targetAudienceType: isActive ? '' : presetId,
        targetAudience: isActive ? '' : preset.label,
      });
    }
  };

  return (
    <div className="space-y-4">
      {showStylePicker && (
        <StylePickerModal
          selected={formData.imageStyle}
          onSelect={(id) => set('imageStyle', id)}
          onClose={() => setShowStylePicker(false)}
        />
      )}

      {/* Auto Mode Toggle */}
      <div
        className={`rounded-2xl border-2 overflow-hidden transition-all duration-300 ${
          formData.autoMode
            ? 'border-orange-400 bg-gradient-to-br from-orange-50 to-amber-50'
            : 'border-stone-200 bg-white'
        }`}
      >
        <button
          onClick={toggleAutoMode}
          className="w-full flex items-center gap-4 px-5 py-4 cursor-pointer text-left"
        >
          {/* Icon */}
          <div
            className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 transition-all duration-300 ${
              formData.autoMode
                ? 'bg-gradient-to-br from-orange-500 to-amber-400'
                : 'bg-stone-100'
            }`}
          >
            <i
              className={`ri-robot-2-line text-lg transition-colors duration-300 ${
                formData.autoMode ? 'text-white' : 'text-stone-400'
              }`}
            />
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span
                className={`text-sm font-bold transition-colors duration-300 ${
                  formData.autoMode ? 'text-orange-700' : 'text-stone-800'
                }`}
              >
                Let AI decide everything
              </span>
              {formData.autoMode && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500 text-white text-xs font-bold">
                  <i className="ri-sparkling-2-fill text-xs" />
                  ON
                </span>
              )}
            </div>
            <p
              className={`text-xs mt-0.5 transition-colors duration-300 ${
                formData.autoMode ? 'text-orange-500' : 'text-stone-400'
              }`}
            >
              Uses your business profile + past posts to generate automatically
            </p>
          </div>

          {/* Toggle switch */}
          <div
            className={`relative w-11 h-6 rounded-full transition-all duration-300 shrink-0 ${
              formData.autoMode ? 'bg-orange-500' : 'bg-stone-200'
            }`}
          >
            <div
              className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all duration-300 ${
                formData.autoMode ? 'left-5' : 'left-0.5'
              }`}
            />
          </div>
        </button>

        {/* Auto Mode active state — minimal prompt */}
        {formData.autoMode && (
          <div className="px-5 pb-4 space-y-3">
            <div className="h-px bg-orange-200" />
            <div className="flex items-start gap-3 bg-white/70 rounded-xl px-4 py-3 border border-orange-200">
              <i className="ri-information-line text-orange-400 text-sm mt-0.5 shrink-0" />
              <p className="text-xs text-orange-700 leading-relaxed">
                AI will use your saved business profile, brand voice, and generation history to craft the best post. You can still optionally hint a topic below.
              </p>
            </div>
            {/* Optional topic hint */}
            <div>
              <label className="block text-xs font-semibold text-orange-700 uppercase tracking-wider mb-1.5">
                Topic Hint
                <span className="normal-case font-normal text-orange-400 ml-1 tracking-normal">
                  (optional — leave blank to let AI choose)
                </span>
              </label>
              <input
                type="text"
                value={formData.offerTopic}
                onChange={(e) => set('offerTopic', e.target.value)}
                placeholder="e.g. Summer sale, new product launch, customer tip..."
                className="w-full text-sm px-4 py-3 rounded-xl border border-orange-200 bg-white text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 transition-all duration-200"
              />
            </div>
            {/* Platform quick-pick in auto mode */}
            <div>
              <label className="block text-xs font-semibold text-orange-700 uppercase tracking-wider mb-1.5">
                Platform
              </label>
              <div className="grid grid-cols-3 gap-1.5">
                {SOCIAL_PLATFORMS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => set('platform', p.id)}
                    className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg border text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      formData.platform === p.id
                        ? 'bg-orange-500 border-orange-500 text-white'
                        : 'bg-white border-orange-200 text-stone-600 hover:border-orange-400 hover:text-orange-600'
                    }`}
                  >
                    <i className={`${p.icon} text-sm shrink-0`} />
                    <span className="truncate">{p.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Output choice */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-50 border border-orange-100">
              <i className="ri-layout-masonry-line text-orange-500 text-sm" />
            </div>
            <div>
              <h3 className="text-xs font-bold text-stone-800 uppercase tracking-wider">Post output</h3>
              <p className="text-xs text-stone-400 mt-0.5">Choose what AI should create</p>
            </div>
          </div>
          <span className="shrink-0 text-xs font-bold text-stone-600 bg-stone-100 px-2 py-1 rounded-lg">
            {totalCost} credits
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => set('includeImage', true)}
            className={`flex items-start gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
              formData.includeImage
                ? 'bg-orange-500 border-orange-500 text-white shadow-sm'
                : 'bg-stone-50 border-stone-200 text-stone-700 hover:border-orange-300 hover:bg-orange-50/40'
            }`}
          >
            <div
              className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${
                formData.includeImage ? 'bg-white/20 text-white' : 'bg-white border border-stone-200 text-orange-500'
              }`}
            >
              <i className="ri-image-line text-base" />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-bold">Caption + AI image</div>
              <div className={`text-xs mt-0.5 leading-tight ${formData.includeImage ? 'text-orange-100' : 'text-stone-400'}`}>
                Includes a generated visual
              </div>
              <div className={`text-xs font-semibold mt-1 ${formData.includeImage ? 'text-white' : 'text-orange-600'}`}>
                +{SOCIAL_IMAGE_COST} credits
              </div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => set('includeImage', false)}
            className={`flex items-start gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
              !formData.includeImage
                ? 'bg-stone-800 border-stone-800 text-white shadow-sm'
                : 'bg-stone-50 border-stone-200 text-stone-700 hover:border-stone-300 hover:bg-white'
            }`}
          >
            <div
              className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${
                !formData.includeImage ? 'bg-white/15 text-white' : 'bg-white border border-stone-200 text-stone-500'
              }`}
            >
              <i className="ri-text-snippet text-base" />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-bold">Caption only</div>
              <div className={`text-xs mt-0.5 leading-tight ${!formData.includeImage ? 'text-stone-300' : 'text-stone-400'}`}>
                Faster, no image generated
              </div>
              <div className={`text-xs font-semibold mt-1 ${!formData.includeImage ? 'text-white' : 'text-stone-500'}`}>
                {SOCIAL_POST_COST} credits
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Profile quick-fill */}
      {!profilesLoading && profiles.length > 0 && (
        <div className="bg-white rounded-2xl border border-stone-100 p-4">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-building-2-line text-orange-400 text-sm" />
            <span className="text-xs font-semibold text-stone-700 uppercase tracking-wider">
              Quick-fill from profile
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {profiles.map((p) => (
              <button
                key={p.id}
                onClick={() => {
                  onProfileSelect?.(p.id);
                  onChange({
                    ...formData,
                    businessName: p.business_name,
                    targetAudience: p.default_audience ?? formData.targetAudience,
                    brandVoice: p.default_tone ?? formData.brandVoice,
                    goal: formData.goal || 'brand-awareness',
                  });
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-all duration-200 cursor-pointer ${
                  selectedProfileId === p.id
                    ? 'border-orange-400 bg-orange-50 ring-1 ring-orange-300'
                    : 'border-stone-200 bg-stone-50 hover:border-orange-300 hover:bg-orange-50'
                }`}
              >
                {p.logo_url ? (
                  <img
                    src={p.logo_url}
                    alt={p.business_name}
                    className="w-6 h-6 rounded-md object-contain shrink-0 bg-white border border-stone-100"
                  />
                ) : (
                  <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-gradient-to-br from-orange-100 to-amber-50 shrink-0">
                    <i className="ri-building-2-line text-orange-400 text-xs" />
                  </div>
                )}
                <span className="text-xs font-semibold text-stone-700">{p.business_name}</span>
                {p.logo_url && (
                  <span className="text-xs text-orange-400 font-medium">
                    <i className="ri-image-line" />
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Auto Mode generate button */}
      {formData.autoMode && (
        <div className="bg-white rounded-2xl border border-stone-100 p-4 space-y-3">
          <div className="flex items-center justify-between px-1">
            <span className="text-xs text-stone-400">Your balance</span>
            <span className={`text-xs font-bold ${hasEnoughCredits(totalCost) ? 'text-stone-700' : 'text-red-500'}`}>
              {creditsRemaining} credits
            </span>
          </div>
          <button
            onClick={onGenerate}
            disabled={!canGenerate || isGenerating}
            className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-sm transition-all duration-200 whitespace-nowrap ${
              canGenerate && !isGenerating
                ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500 cursor-pointer'
                : 'bg-stone-100 text-stone-400 cursor-not-allowed'
            }`}
          >
            {isGenerating ? (
              <>
                <i className="ri-loader-4-line text-base animate-spin" />
                AI is crafting your post...
              </>
            ) : (
              <>
                <i className="ri-robot-2-line text-base" />
                Auto-Generate — {totalCost} Credits
              </>
            )}
          </button>
          {isGenerating && (
            <button
              type="button"
              onClick={onStopGeneration}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-stone-300 bg-white text-stone-700 hover:border-red-300 hover:bg-red-50 hover:text-red-700 font-bold text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-stop-circle-line text-base" />
              Stop and edit form
            </button>
          )}
          {!hasEnoughCredits(totalCost) && !isGenerating && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2.5 rounded-xl">
              <i className="ri-error-warning-line text-sm shrink-0" />
              <span className="flex-1">Not enough credits.</span>
              <Link to="/pricing" className="shrink-0 text-xs font-bold text-red-600 underline underline-offset-2 whitespace-nowrap cursor-pointer">
                Upgrade
              </Link>
            </div>
          )}
          <div className="flex items-center justify-center gap-1.5 text-xs text-stone-400">
            <i className="ri-sparkling-2-line text-stone-300" />
            <span>
              {SOCIAL_POST_COST} credits for captions
              {formData.includeImage && (
                <> · <strong className="text-stone-600">+{SOCIAL_IMAGE_COST}</strong> for image</>
              )}
            </span>
          </div>
        </div>
      )}

      {/* Main form card — hidden in Auto Mode */}
      <div className={`bg-white rounded-2xl border border-stone-100 overflow-hidden transition-all duration-300 ${formData.autoMode ? 'hidden' : ''}`}>
        <div className="px-5 py-4 border-b border-stone-100">
          <h2 className="text-sm font-bold text-stone-900">Post Details</h2>
          <p className="text-xs text-stone-400 mt-0.5">
            Tell us who this is for and what you want to post
          </p>
        </div>

        <div className="px-5 py-5 space-y-4">
          <CollapsibleSection
            icon="ri-user-settings-line"
            title="Identity"
            subtitle="Account type, profile, and platform"
            defaultOpen
          >
          {/* Identity Type */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Identity Type <span className="text-orange-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {IDENTITY_TYPE_OPTIONS.map((identity) => (
                <button
                  key={identity.id}
                  onClick={() => set('identityType', identity.id)}
                  className={`flex flex-col items-start gap-1 px-3 py-2.5 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
                    formData.identityType === identity.id
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:bg-orange-50/50'
                  }`}
                >
                  <i className={`${identity.icon} text-sm`} />
                  <span className="text-xs font-bold">{identity.label}</span>
                  <span className={`text-xs leading-tight ${formData.identityType === identity.id ? 'text-orange-100' : 'text-stone-400'}`}>
                    {identity.desc}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Business Name */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              {formData.identityType === 'creator' ? 'Creator / Brand Name' : formData.identityType === 'hybrid' ? 'Creator or Business Name' : 'Business Name'} <span className="text-orange-500">*</span>
            </label>
            <input
              type="text"
              value={formData.businessName}
              onChange={(e) => set('businessName', e.target.value)}
              placeholder={formData.identityType === 'creator' ? 'e.g. Coach Maya Fit' : 'e.g. Rivera Plumbing Co.'}
              className={inputClass}
            />
          </div>

          {formData.identityType !== 'business' && (
            <div className="rounded-xl border border-stone-200 bg-stone-50 px-4 py-4 space-y-4">
              <div className="flex items-center gap-2">
                <i className="ri-user-star-line text-orange-400 text-sm" />
                <span className="text-xs font-bold text-stone-800 uppercase tracking-wider">Creator Profile</span>
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                  Niche
                </label>
                <input
                  type="text"
                  value={formData.creatorNiche}
                  onChange={(e) => set('creatorNiche', e.target.value)}
                  placeholder="e.g. fitness, gaming, lifestyle, business, parenting"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                  Personality
                </label>
                <div className="flex flex-wrap gap-2">
                  {CREATOR_PERSONALITY_OPTIONS.map((personality) => (
                    <button
                      key={personality}
                      onClick={() => set('creatorPersonality', formData.creatorPersonality === personality ? '' : personality)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        formData.creatorPersonality === personality
                          ? 'bg-orange-500 border-orange-500 text-white'
                          : 'bg-white border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                      }`}
                    >
                      {personality}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                  Content Pillars
                </label>
                <textarea
                  value={formData.contentPillars}
                  onChange={(e) => set('contentPillars', e.target.value)}
                  placeholder="e.g. quick workouts, honest nutrition, beginner confidence, behind-the-scenes creator life"
                  rows={2}
                  maxLength={300}
                  className={`${inputClass} resize-none bg-white`}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                    Audience Type
                  </label>
                  <input
                    type="text"
                    value={formData.creatorAudienceType}
                    onChange={(e) => set('creatorAudienceType', e.target.value)}
                    placeholder="e.g. beginners, fans, entrepreneurs, locals"
                    className={`${inputClass} bg-white`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                    Tone
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {CREATOR_TONE_OPTIONS.map((tone) => (
                      <button
                        key={tone}
                        onClick={() => set('creatorTone', formData.creatorTone === tone ? '' : tone)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                          formData.creatorTone === tone
                            ? 'bg-orange-500 border-orange-500 text-white'
                            : 'bg-white border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                        }`}
                      >
                        {tone}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Platform */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Platform <span className="text-orange-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {SOCIAL_PLATFORMS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => set('platform', p.id)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    formData.platform === p.id
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                  }`}
                >
                  <i className={`${p.icon} text-sm shrink-0`} />
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          </CollapsibleSection>

          <CollapsibleSection
            icon="ri-quill-pen-line"
            title="Content Brief"
            subtitle="Type, topic, audience, goal, and style"
          >
          {/* Post Type */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Post Type <span className="text-orange-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {POST_TYPES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => set('postType', t.id)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
                    formData.postType === t.id
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:bg-orange-50/50'
                  }`}
                >
                  <i className={`${t.icon} text-sm shrink-0`} />
                  <div>
                    <div className="text-xs font-bold">{t.label}</div>
                    <div
                      className={`text-xs font-normal mt-0.5 ${formData.postType === t.id ? 'text-orange-100' : 'text-stone-400'}`}
                    >
                      {t.desc}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Topic Seed */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Topic / Prompt Seed
              <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">
                (optional but helpful)
              </span>
            </label>
            <textarea
              value={formData.offerTopic}
              onChange={(e) => set('offerTopic', e.target.value)}
              placeholder="e.g. new service launch, founder lesson, customer story, seasonal offer, audience question"
              rows={3}
              maxLength={400}
              className={`${inputClass} resize-none`}
            />
            <div className="text-right text-xs text-stone-400 mt-1">
              {formData.offerTopic.length}/400
            </div>
          </div>

          {/* Target Audience */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Who is this post for?
              <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">
                (optional)
              </span>
            </label>
            {/* Quick-select presets */}
            <div className="grid grid-cols-2 gap-2 mb-2.5">
              {TARGET_AUDIENCE_PRESETS.map((preset) => {
                const isActive = formData.targetAudienceType === preset.id;
                return (
                  <button
                    key={preset.id}
                    onClick={() => handleAudiencePreset(preset.id)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-orange-500 border-orange-500 text-white'
                        : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                    }`}
                  >
                    <i className={`${preset.icon} text-sm shrink-0`} />
                    {preset.label}
                  </button>
                );
              })}
            </div>
            {/* Custom / Niche text input — shown when niche is selected or user wants custom */}
            {(formData.targetAudienceType === 'niche' || (formData.targetAudienceType === '' && formData.targetAudience && !TARGET_AUDIENCE_PRESETS.some(p => p.label === formData.targetAudience))) && (
              <input
                type="text"
                value={formData.targetAudience}
                onChange={(e) => set('targetAudience', e.target.value)}
                placeholder={formData.targetAudienceType === 'niche' ? 'Describe your niche audience...' : 'e.g. Homeowners aged 30–55 in Austin, TX'}
                className={inputClass}
                autoFocus={formData.targetAudienceType === 'niche'}
              />
            )}
            {/* Show custom input link when nothing is selected */}
            {formData.targetAudienceType === '' && !formData.targetAudience && (
              <button
                onClick={() => onChange({ ...formData, targetAudienceType: 'niche' })}
                className="text-xs text-stone-400 hover:text-orange-500 transition-colors cursor-pointer flex items-center gap-1"
              >
                <i className="ri-add-line text-sm" />
                Or describe a custom audience
              </button>
            )}
          </div>

          {/* Goal */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Business Goal
              <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">
                (optional)
              </span>
            </label>
            <div className="flex flex-wrap gap-2 mb-2.5">
              {CAMPAIGN_GOAL_OPTIONS.map((goal) => (
                <button
                  key={goal.id}
                  onClick={() => set('goal', formData.goal === goal.id ? '' : goal.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    formData.goal === goal.id
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                  }`}
                >
                  <i className={`${goal.icon} mr-1 text-xs`} />
                  {goal.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={formData.goal}
              onChange={(e) => set('goal', e.target.value)}
              placeholder="e.g. fill seats on Thursday nights, increase repeat visits, push Mother's Day brunch"
              className={inputClass}
            />
          </div>

          {/* Theme + Event */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                Theme
                <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">(optional)</span>
              </label>
              <div className="flex flex-wrap gap-2 mb-2.5">
                {SOCIAL_POST_THEME_OPTIONS.map((theme) => (
                  <button
                    key={theme.id}
                    onClick={() => set('theme', formData.theme === theme.id ? '' : theme.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      formData.theme === theme.id
                        ? 'bg-orange-500 border-orange-500 text-white'
                        : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                    }`}
                  >
                    {theme.label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={formData.theme}
                onChange={(e) => set('theme', e.target.value)}
                placeholder="e.g. local pride, beginner tips, customer story, seasonal, community"
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                Event / Promotion
                <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">(optional)</span>
              </label>
              <textarea
                value={formData.eventPromotion}
                onChange={(e) => set('eventPromotion', e.target.value)}
                placeholder="e.g. weekend event, teacher appreciation offer, product drop, limited booking window"
                rows={3}
                maxLength={250}
                className={`${inputClass} resize-none`}
              />
            </div>
          </div>

          {/* Content Style */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Creative Style Engine
              <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">(optional)</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {CONTENT_STYLE_OPTIONS.map((style) => (
                <button
                  key={style.id}
                  onClick={() => set('contentStyle', formData.contentStyle === style.id ? '' : style.id)}
                  className={`flex flex-col items-start gap-0.5 px-3 py-2.5 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
                    formData.contentStyle === style.id
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:bg-orange-50/50'
                  }`}
                >
                  <span className="text-xs font-bold">{style.label}</span>
                  <span className={`text-xs leading-tight ${formData.contentStyle === style.id ? 'text-orange-100' : 'text-stone-400'}`}>
                    {style.desc}
                  </span>
                </button>
              ))}
            </div>
          </div>
          </CollapsibleSection>

          <CollapsibleSection
            icon="ri-layout-4-line"
            title="Format & Visuals"
            subtitle="Caption length and visual style"
          >
          {/* Reference style */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Reference Style
              <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">(optional)</span>
            </label>
            <div className="grid grid-cols-2 gap-2 mb-2.5">
              {REFERENCE_STYLE_OPTIONS.map((style) => (
                <button
                  key={style.id}
                  onClick={() => set('referenceStyle', formData.referenceStyle === style.id ? '' : style.id)}
                  className={`flex flex-col items-start gap-0.5 px-3 py-2.5 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
                    formData.referenceStyle === style.id
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:bg-orange-50/50'
                  }`}
                >
                  <span className="text-xs font-bold">{style.label}</span>
                  <span className={`text-xs ${formData.referenceStyle === style.id ? 'text-orange-100' : 'text-stone-400'}`}>
                    {style.desc}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Caption Length */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Caption Length
            </label>
            <div className="grid grid-cols-3 gap-2">
              {CAPTION_LENGTHS.map((cl) => (
                <button
                  key={cl.id}
                  onClick={() => set('captionLength', cl.id)}
                  className={`flex flex-col items-center gap-1 py-3 px-2 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                    formData.captionLength === cl.id
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300'
                  }`}
                >
                  <i className={`${cl.icon} text-base`} />
                  <span className="text-xs font-bold">{cl.label}</span>
                  <span
                    className={`text-xs font-normal ${formData.captionLength === cl.id ? 'text-orange-100' : 'text-stone-400'}`}
                  >
                    {cl.desc}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Image output is selected in the prominent Post output control above. */}
          <div className="hidden">
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Include AI Image?
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => set('includeImage', true)}
                className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border-2 text-xs font-semibold transition-all duration-200 cursor-pointer ${
                  formData.includeImage
                    ? 'bg-orange-500 border-orange-500 text-white'
                    : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300'
                }`}
              >
                <i className="ri-image-line text-sm" />
                <div className="text-left">
                  <div>Yes, generate image</div>
                  <div
                    className={`font-normal text-xs mt-0.5 ${formData.includeImage ? 'text-orange-100' : 'text-stone-400'}`}
                  >
                    +{SOCIAL_IMAGE_COST} credits
                  </div>
                </div>
              </button>
              <button
                onClick={() => set('includeImage', false)}
                className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border-2 text-xs font-semibold transition-all duration-200 cursor-pointer ${
                  !formData.includeImage
                    ? 'bg-stone-700 border-stone-700 text-white'
                    : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-stone-300'
                }`}
              >
                <i className="ri-text-snippet text-sm" />
                <div className="text-left">
                  <div>Caption only</div>
                  <div
                    className={`font-normal text-xs mt-0.5 ${!formData.includeImage ? 'text-stone-300' : 'text-stone-400'}`}
                  >
                    Faster generation
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Image Style — only shown when includeImage is true */}
          {formData.includeImage && (
            <div>
              <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                Image Style
                <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">
                  (optional)
                </span>
              </label>
              <button
                onClick={() => setShowStylePicker(true)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-stone-200 bg-stone-50 hover:border-orange-300 hover:bg-orange-50/30 transition-all duration-200 cursor-pointer text-left"
              >
                {selectedStyleConfig ? (
                  <>
                    <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 border border-stone-200">
                      <img
                        src={selectedStyleConfig.previewImage}
                        alt={selectedStyleConfig.label}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold text-stone-800">{selectedStyleConfig.label}</div>
                      <div className="text-xs text-stone-400 truncate">{selectedStyleConfig.description}</div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); set('imageStyle', ''); }}
                      className="w-5 h-5 flex items-center justify-center rounded-full bg-stone-200 hover:bg-stone-300 text-stone-500 shrink-0 cursor-pointer"
                    >
                      <i className="ri-close-line text-xs" />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="w-10 h-10 rounded-lg bg-stone-100 border border-stone-200 flex items-center justify-center shrink-0">
                      <i className="ri-palette-line text-stone-400 text-base" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-stone-500">No style selected</div>
                      <div className="text-xs text-stone-400">AI will choose the best style</div>
                    </div>
                    <i className="ri-arrow-right-s-line text-stone-400 text-base shrink-0" />
                  </>
                )}
              </button>
            </div>
          )}
          </CollapsibleSection>

          {/* Advanced (collapsible) */}
          <CollapsibleSection
            icon="ri-settings-3-line"
            title="Advanced"
            subtitle="Voice & creative direction"
            badge={advancedBadge}
          >
            <div>
              <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                Brand Voice
              </label>
              <div className="flex flex-wrap gap-2">
                {BRAND_VOICES.map((v) => (
                  <button
                    key={v}
                    onClick={() => set('brandVoice', formData.brandVoice === v ? '' : v)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      formData.brandVoice === v
                        ? 'bg-orange-500 border-orange-500 text-white'
                        : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                Creative Direction
                <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">
                  (optional)
                </span>
              </label>
              <textarea
                value={formData.creativeDirection}
                onChange={(e) => set('creativeDirection', e.target.value)}
                placeholder="e.g. Show a before/after transformation, use warm summer tones, feature a happy customer..."
                rows={2}
                maxLength={300}
                className={`${inputClass} resize-none`}
              />
            </div>

            {/* Reference Image Upload */}
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider">
                  Reference Image
                </label>
                <span className="text-xs font-normal text-stone-400 normal-case tracking-normal">(optional)</span>
              </div>
              <p className="text-xs text-stone-400 mb-2.5 leading-relaxed">
                Upload a product photo or brand image. The AI will incorporate or recreate it in the generated visual.
              </p>

              {/* Hidden file input */}
              <input
                ref={referenceImageInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={handleReferenceImageUpload}
              />

              {formData.referenceImageBase64 ? (
                /* Preview state */
                <div className="rounded-xl border border-stone-200 overflow-hidden bg-stone-50">
                  <div className="relative">
                    <img
                      src={`data:image/png;base64,${formData.referenceImageBase64}`}
                      alt="Reference"
                      className="w-full h-36 object-contain bg-stone-100"
                    />
                    <div className="absolute top-2 right-2 flex items-center gap-1.5">
                      <button
                        onClick={() => referenceImageInputRef.current?.click()}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white/90 border border-stone-200 text-xs font-semibold text-stone-600 hover:bg-white transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-refresh-line text-xs" />
                        Replace
                      </button>
                      <button
                        onClick={clearReferenceImage}
                        className="w-6 h-6 flex items-center justify-center rounded-lg bg-white/90 border border-stone-200 text-stone-500 hover:text-red-500 hover:border-red-200 transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-xs" />
                      </button>
                    </div>
                    <div className="absolute bottom-2 left-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500 text-white text-xs font-bold">
                        <i className="ri-image-line text-xs" />
                        Reference uploaded
                      </span>
                    </div>
                  </div>
                  {/* Prompt for how to use the image */}
                  <div className="px-3 py-3 border-t border-stone-100">
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                      How should AI use this image?
                    </label>
                    <input
                      type="text"
                      value={formData.referenceImagePrompt}
                      onChange={(e) => onChange({ ...formData, referenceImagePrompt: e.target.value })}
                      placeholder="e.g. Recreate this exact can of beer in the scene, keep the label visible..."
                      className="w-full text-xs px-3 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 transition-all duration-200"
                      maxLength={200}
                    />
                    <p className="text-xs text-stone-400 mt-1.5 leading-relaxed">
                      Be specific — e.g. &quot;use this product as the hero of the image&quot;, &quot;recreate this exact label&quot;, &quot;place this item in a lifestyle scene&quot;
                    </p>
                  </div>
                </div>
              ) : (
                /* Upload prompt */
                <button
                  onClick={() => referenceImageInputRef.current?.click()}
                  className="w-full flex flex-col items-center gap-2 px-4 py-5 rounded-xl border-2 border-dashed border-stone-200 bg-stone-50 hover:border-orange-300 hover:bg-orange-50/30 transition-all duration-200 cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-stone-200 group-hover:border-orange-200 transition-colors">
                    <i className="ri-image-add-line text-stone-400 group-hover:text-orange-400 text-lg transition-colors" />
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-semibold text-stone-600 group-hover:text-orange-600 transition-colors">
                      Upload reference image
                    </p>
                    <p className="text-xs text-stone-400 mt-0.5">
                      PNG, JPG, WEBP — product photos, logos, brand assets
                    </p>
                  </div>
                </button>
              )}
            </div>
          </CollapsibleSection>

          {/* Extras (collapsible) */}
          <CollapsibleSection
            icon="ri-hashtag"
            title="Extras"
            subtitle="CTA, hashtags & special instructions"
            badge={extrasBadge}
          >
            <div>
              <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                Desired CTA
                <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">
                  (optional)
                </span>
              </label>
              <input
                type="text"
                value={formData.cta}
                onChange={(e) => set('cta', e.target.value)}
                placeholder="e.g. Book now, DM us, Link in bio"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                Hashtag Preferences
                <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">
                  (optional)
                </span>
              </label>
              <input
                type="text"
                value={formData.hashtags}
                onChange={(e) => set('hashtags', e.target.value)}
                placeholder="e.g. #plumbing #austintx #homerepair"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                Special Instructions
                <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">
                  (optional)
                </span>
              </label>
              <textarea
                value={formData.specialInstructions}
                onChange={(e) => set('specialInstructions', e.target.value)}
                placeholder="Any extra context or requirements..."
                rows={2}
                maxLength={300}
                className={`${inputClass} resize-none`}
              />
            </div>
          </CollapsibleSection>

          {/* Generate button */}
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <span className="text-xs text-stone-400">Your balance</span>
              <span
                className={`text-xs font-bold ${hasEnoughCredits(totalCost) ? 'text-stone-700' : 'text-red-500'}`}
              >
                {creditsRemaining} credits
              </span>
            </div>
            <button
              onClick={onGenerate}
              disabled={!canGenerate || isGenerating}
              className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-sm transition-all duration-200 whitespace-nowrap ${
                canGenerate && !isGenerating
                  ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500 cursor-pointer'
                  : 'bg-stone-100 text-stone-400 cursor-not-allowed'
              }`}
            >
              {isGenerating ? (
                <>
                  <i className="ri-loader-4-line text-base animate-spin" />
                  Generating your post...
                </>
              ) : (
                <>
                  <i className="ri-sparkling-2-fill text-base" />
                  Generate Social Post — {totalCost} Credits
                </>
              )}
            </button>
            {isGenerating && (
              <button
                type="button"
                onClick={onStopGeneration}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-stone-300 bg-white text-stone-700 hover:border-red-300 hover:bg-red-50 hover:text-red-700 font-bold text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-stop-circle-line text-base" />
                Stop and edit form
              </button>
            )}
            {!hasEnoughCredits(totalCost) && !isGenerating && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2.5 rounded-xl">
                <i className="ri-error-warning-line text-sm shrink-0" />
                <span className="flex-1">Not enough credits. Please upgrade or purchase more.</span>
                <Link
                  to="/pricing"
                  className="shrink-0 text-xs font-bold text-red-600 underline underline-offset-2 whitespace-nowrap cursor-pointer"
                >
                  Upgrade
                </Link>
              </div>
            )}
            <div className="flex items-center justify-center gap-1.5 text-xs text-stone-400">
              <i className="ri-sparkling-2-line text-stone-300" />
              <span>
                {SOCIAL_POST_COST} credits for captions
                {formData.includeImage && (
                  <> · <strong className="text-stone-600">+{SOCIAL_IMAGE_COST}</strong> for image</>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
